/**
 * L1: how the estate has grown, from the persisted daily rollups.
 *
 * `estate_day` has held one whole-estate snapshot per day since the board
 * shipped and been read by nothing. This is the read side: resources over
 * time, plus the signed movement against the oldest recorded day. No Azure
 * call — the numbers are the ones already on disk.
 *
 * Honesty: growth is **signed**, because an estate that shrank is a different
 * story from one that grew; a delta needs two days that both carry the field,
 * so a single rollup shows an em dash rather than a comfortable zero; and a
 * day whose payload predates a field renders as a gap in the line, never as a
 * cliff to nought.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { EstateGrowth } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { formatDay, formatDelta } from "./status";

const GROWTH_CONFIG = {
  resources: { label: "Resources", color: "var(--norican-chart-1)" },
};

interface EstateGrowthPanelProps {
  /** The readback, or null when the history table could not be read. */
  growth: EstateGrowth | null;
}

export function EstateGrowthPanel({ growth }: EstateGrowthPanelProps) {
  if (growth == null) {
    return (
      <PanelCard title="Estate growth" caption="Unknown">
        <Typography variant="body-sm" tone="muted">
          The persisted daily rollups could not be read, so estate growth is unknown — which is not
          the same as the estate having stood still. The reason is listed with this snapshot&apos;s
          problems.
        </Typography>
      </PanelCard>
    );
  }

  const rows = growth.points.map((point) => ({ ...point, label: formatDay(point.day) }));
  const charted = rows.filter((row) => row.resources != null);
  const baseline = growth.baseline_day ? formatDay(growth.baseline_day) : null;

  return (
    <div className="flex flex-col gap-4">
      <PanelCard
        title="Estate growth"
        caption={`${formatCount(growth.days_recorded)} of ${formatCount(
          growth.window_days,
        )} days recorded`}
      >
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic
            label="Resources since baseline"
            value={formatDelta(growth.resources_delta)}
            tone={
              growth.resources_delta == null
                ? "neutral"
                : growth.resources_delta > 0
                  ? "accent"
                  : "neutral"
            }
            size="lg"
            caption={
              growth.resources_delta == null
                ? "Two recorded days are needed before growth means anything"
                : `Against ${baseline ?? "the baseline"}`
            }
          />
          <Statistic
            label="Workloads since baseline"
            value={formatDelta(growth.workloads_delta)}
            caption={
              growth.workloads_delta == null
                ? "Not yet comparable"
                : "Container apps, sites, databases and VMs"
            }
          />
          <Statistic
            label="Baseline day"
            value={baseline ?? "—"}
            caption={
              growth.days_recorded === 0
                ? "The daily rollup has not run yet"
                : "Oldest rollup in the window"
            }
          />
          <Statistic
            label="Latest rollup"
            value={growth.latest_day ? formatDay(growth.latest_day) : "—"}
            caption="Written by the poller's daily tier"
          />
        </div>
      </PanelCard>

      <ChartCard
        title="Resources over time"
        caption={`Daily rollups, last ${growth.window_days} days`}
        tableTwin={{
          id: "estate-growth",
          ariaLabel: "Estate size per recorded day",
          data: rows,
          getRowId: (row) => row.day,
          columns: [
            { id: "day", header: "Day", accessor: (row) => row.label },
            {
              id: "resources",
              header: "Resources",
              accessor: (row) => row.resources,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.resources)}</span>,
            },
            {
              id: "groups",
              header: "Resource groups",
              accessor: (row) => row.resource_groups,
              align: "end",
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.resource_groups)}</span>
              ),
            },
            {
              id: "workloads",
              header: "Workloads",
              accessor: (row) => row.workloads,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.workloads)}</span>,
            },
            {
              id: "subs",
              header: "Subscriptions readable",
              accessor: (row) => row.subscriptions_reachable,
              align: "end",
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.subscriptions_reachable)}</span>
              ),
            },
          ],
        }}
      >
        {charted.length < 2 ? (
          <EmptyState
            size="sm"
            title="Not enough history for a growth trend yet"
            description="The poller writes one estate rollup a day; two are needed before a line says anything."
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Chart.Container
              config={GROWTH_CONFIG}
              className="h-64"
              aria-label={`Estate resource count per recorded day, last ${growth.window_days} days`}
            >
              <Chart.AreaChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                <Chart.YAxis {...Chart.axisProps} width={48} allowDecimals={false} />
                <Chart.Tooltip content={<Chart.TooltipContent />} />
                <Chart.Area
                  {...Chart.seriesDefaults}
                  dataKey="resources"
                  type="monotone"
                  stroke="var(--color-resources)"
                  fill="var(--color-resources)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                  dot={false}
                />
              </Chart.AreaChart>
            </Chart.Container>
            <Typography variant="body-sm" tone="muted">
              A break in the area is a day with no rollup, or a rollup written before this field
              existed — not a day the estate emptied out.
            </Typography>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
